import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import { Link } from "react-router-dom";
import config from "../../config";
import makeRequest from "../../makeRequest";

const BASE = config.api.baseUrl;
const ICON_FALLBACK = "fa-solid fa-stethoscope";

/* ─── Single card ─────────────────────────────────────── */
function SpecialityCard({ item }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [iconFailed, setIconFailed] = useState(false);
  const iconRef = useRef(null);

  useEffect(() => {
    if (!item.hasImage) return;
    let url = null;
    fetch(`${BASE}/website/speciality/${item.id}/image`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob) {
          url = URL.createObjectURL(blob);
          setImgSrc(url);
        }
      })
      .catch(() => {});
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [item.id, item.hasImage]);

  // Detect if the FA glyph actually rendered — invalid/Pro-only classes render empty
  useEffect(() => {
    const el = iconRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      const before = window.getComputedStyle(el, "::before");
      const content = before.getPropertyValue("content");
      if (!content || content === "none" || content === '""' || content === "''") {
        setIconFailed(true);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  const icon = item.icon?.trim() || ICON_FALLBACK;

  return (
    <Link to={`/speciality/${item.id}`} className="mh-spec-card">
      <div className="mh-spec-card__visual">
        {imgSrc && <img src={imgSrc} alt={item.name} className="mh-spec-card__photo" />}
        <div className="mh-spec-card__icon-wrap">
          {iconFailed ? (
            <svg
              className="mh-spec-card__icon-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
          ) : (
            <i ref={iconRef} className={icon} />
          )}
        </div>
      </div>
      <div className="mh-spec-card__body">
        <h5 className="mh-spec-card__label">{item.name}</h5>
        <p className="mh-spec-card__sub">Expert care &amp; treatment</p>
        <span className="mh-spec-card__btn">
          View Details
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="8" x2="13" y2="8" />
            <polyline points="8 3 13 8 8 13" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

/* ─── Skeleton placeholder ────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="mh-spec-card mh-spec-card--skeleton">
      <div className="mh-spec-sk-top" />
      <div className="mh-spec-card__body">
        <div className="mh-spec-sk-line mh-spec-sk-line--wide" />
        <div className="mh-spec-sk-line" />
        <div className="mh-spec-sk-line mh-spec-sk-line--btn" />
      </div>
    </div>
  );
}

/* ─── Section ─────────────────────────────────────────── */
const Service = () => {
  const [specialities, setSpecialities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    makeRequest({ route: "/website/speciality/public", method: "get" })
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) setSpecialities(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleOpenSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleCloseSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  const filtered = query.trim() ? specialities.filter((s) => s.name.toLowerCase().includes(query.trim().toLowerCase())) : specialities;

  return (
    <section className="mh-spec-section">
      <div className="container">
        {/* Header */}
        <div className="mh-spec-header">
          <div>
            <span className="mh-lf-section__eyebrow">
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M10 2a1 1 0 011 1v5h5a1 1 0 010 2h-5v5a1 1 0 01-2 0v-5H4a1 1 0 010-2h5V3a1 1 0 011-1z" />
              </svg>
              Compassionate Expert Care
            </span>
            <h2 className="mh-lf-section__title" style={{ marginBottom: 0 }}>
              Clinical Specialities
            </h2>
          </div>

          <div className="mh-spec-header__right">
            {/* Expandable search bar */}
            <div className={`mh-spec-search-wrap${searchOpen ? " mh-spec-search-wrap--open" : ""}`}>
              <svg
                className="mh-spec-search-wrap__icon"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="8.5" cy="8.5" r="5.5" />
                <line x1="13" y1="13" x2="18" y2="18" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                className="mh-spec-search-wrap__input"
                placeholder="Search specialities…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search specialities"
              />
              <button className="mh-spec-search-wrap__close" onClick={handleCloseSearch} aria-label="Close search">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="4" y1="4" x2="16" y2="16" />
                  <line x1="16" y1="4" x2="4" y2="16" />
                </svg>
              </button>
            </div>

            {!searchOpen && (
              <>
                <Link to="/service" className="mh-spec-view-btn">
                  View All
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="10" x2="16" y2="10" />
                    <polyline points="10 4 16 10 10 16" />
                  </svg>
                </Link>
                <button className="mh-spec-search-btn" onClick={handleOpenSearch} aria-label="Search specialities">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8.5" cy="8.5" r="5.5" />
                    <line x1="13" y1="13" x2="18" y2="18" />
                  </svg>
                </button>
                <div className="mh-spec-nav-group">
                  <button ref={prevRef} className="mh-spec-nav-btn" aria-label="Previous">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>
                  <button ref={nextRef} className="mh-spec-nav-btn" aria-label="Next">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 6 15 12 9 18" />
                    </svg>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Slider / Search results */}
        <div className="mh-spec-slider-wrap" style={{ marginBottom: 32 }}>
          {loading ? (
            <div className="mh-spec-skeleton-row">
              {Array(5)
                .fill(null)
                .map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
            </div>
          ) : searchOpen ? (
            filtered.length > 0 ? (
              <div className="mh-spec-grid">
                {filtered.map((item) => (
                  <SpecialityCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="mh-spec-empty">
                <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="21" cy="21" r="14" />
                  <line x1="31" y1="31" x2="43" y2="43" />
                  <line x1="16" y1="21" x2="26" y2="21" />
                </svg>
                <p>
                  No specialities match <strong>"{query}"</strong>
                </p>
              </div>
            )
          ) : (
            <Swiper
              modules={[Autoplay, Navigation]}
              slidesPerView={5}
              spaceBetween={20}
              loop={specialities.length > 5}
              autoplay={{ delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true }}
              navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
              onSwiper={(swiper) => {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
                swiper.navigation.init();
                swiper.navigation.update();
              }}
              speed={650}
              breakpoints={{
                1200: { slidesPerView: 5, spaceBetween: 20 },
                992: { slidesPerView: 4, spaceBetween: 16 },
                768: { slidesPerView: 3, spaceBetween: 14 },
                480: { slidesPerView: 2, spaceBetween: 12 },
                0: { slidesPerView: 1, spaceBetween: 12 },
              }}
            >
              {specialities.map((item) => (
                <SwiperSlide key={item.id}>
                  <SpecialityCard item={item} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </div>
    </section>
  );
};

export default Service;
